import React, { useMemo, useContext } from "react"
import ConditionalRender from "../../../ConditionalRender.js"
import { Icons } from "../Icons/index.js"
import { ClickableDropdown, DropdownContext } from "../Dropdown/Dropdown.jsx"
import { DropdownMenu } from "../Dropdown/DropdownMenu.jsx"
import { DropdownItem } from "../Dropdown/DropdownItem.jsx"

/**
 * Mirrors InputBox size classes so the trigger looks identical to an InputBox.
 */
const SelectBoxSizes = {
    Small: "input-box-small",
    Medium: "input-box-medium",
    Large: "input-box-large",
}

const SelectBoxSizeFonts = {
    Small: "body-s-semibold",
    Medium: "body-s-semibold",
    Large: "body-m-semibold",
}

const SelectBoxTrigger = ({ triggerClasses, selectedLabel, placeholder, triggerStyle, placeholderClass, placeholderStyle }) => {
    const { open } = useContext(DropdownContext)
    return (
        <div className={triggerClasses} style={triggerStyle}>
            <span
                className={selectedLabel ? "" : placeholderClass}
                style={selectedLabel ? undefined : placeholderStyle}
            >
                {selectedLabel || placeholder}
            </span>
            <Icons name={open ? "chevron-up" : "chevron-down"} size={16} className="ml-s" />
        </div>
    )
}

/**
 * SelectBox — Single-select dropdown input.
 *
 * Trigger is styled with the same input-box classes as InputBox/InputArea.
 * The menu and items are the DS DropdownMenu/DropdownItem components —
 * no custom CSS for those.
 *
 * Options accept plain strings or {label, value} objects.
 * handleOnChange receives the selected value directly (not a native event).
 *
 * @param {string}   Size           - 'Small' | 'Medium' | 'Large'
 * @param {string}   label          - Label text shown above the trigger
 * @param {boolean}  showLabel      - Whether to render the label
 * @param {string}   placeholder    - Shown in the trigger when no value is selected
 * @param {Array}    options        - string[] or {label, value}[]
 * @param {string}   value          - Controlled selected value
 * @param {func}     handleOnChange - Called with the selected value (string)
 * @param {string}   error          - Triggers error styling on the trigger
 * @param {boolean}  disabled       - Disabled state
 * @param {string}   variant        - 'default' (border + bg) | 'ghost' (no border, no bg, hover only)
 */
export const SelectBox = ({
    Size = "Medium",
    label = "",
    showLabel = false,
    labelClass = "input-box-label body-s-semibold",
    showHelperText = false,
    helperText = "",
    helperTextClass = "helper-text support-m",
    helperTextStyle = {},
    value,
    placeholder = "Select...",
    placeholderClass = "select-box__placeholder",
    placeholderStyle = {},
    options = [],
    customClass = "",
    customStyle = {},
    handleOnChange,
    error = "",
    disabled = false,
    fontClass = null,
    variant = "default",
    containerClass = "",
    triggerStyle = {},
    menuCustomStyle = {},
    align = "right"
}) => {
    const triggerClasses = useMemo(() =>
        [
            "input-box",
            SelectBoxSizes[Size],
            "select-box__trigger",
            fontClass ?? SelectBoxSizeFonts[Size],
            variant === "ghost" && "select-box__trigger--ghost",
            error && "input-box-error",
            disabled && "input-box-disabled",
            customClass,
        ].filter(Boolean).join(" "),
        [Size, fontClass, variant, error, disabled, customClass]
    )

    const normalizedOptions = useMemo(() =>
        options.map(opt =>
            typeof opt === "string" ? { label: opt, value: opt } : opt
        ),
        [options]
    )

    const selectedLabel = normalizedOptions.find(opt => opt.value === value)?.label

    return (
        <div className={containerClass} style={{ ...customStyle }}>
            <ConditionalRender if={showLabel && label !== ""}>
                <div className={labelClass}>{label}</div>
            </ConditionalRender>
            <ClickableDropdown disabled={disabled}>
                <SelectBoxTrigger
                    triggerClasses={triggerClasses}
                    triggerStyle={triggerStyle}
                    selectedLabel={selectedLabel}
                    placeholder={placeholder}
                    placeholderClass={placeholderClass}
                    placeholderStyle={placeholderStyle}
                />
                <DropdownMenu usePortal align={align} customStyle={menuCustomStyle}>
                    {normalizedOptions.map(opt => (
                        <DropdownItem
                            key={opt.value}
                            disabled={disabled}
                            onClick={() => handleOnChange?.(opt.value)}
                            selected={opt.value === value}
                        >
                            {opt.label}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </ClickableDropdown>
            <ConditionalRender if={showHelperText && helperText !== ""}>
                <div className={helperTextClass} style={helperTextStyle}>
                    {helperText}
                </div>
            </ConditionalRender>
        </div>
    )
}
